// dashboard/ContentGrid.tsx
import React from "react";
import { motion } from "framer-motion";
import { Layout, Card, Dropdown, Button, Empty } from "antd";
import {
  MoreOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { useTranslation } from "react-i18next";
import { InsightCard, CardType } from "../../../types/dashboard";

const { Content } = Layout;

const Grid: React.FC<{ children: React.ReactNode[] }> = ({ children }) => {
  const items = React.Children.toArray(children).slice(0, 10);
  return (
    <div className="dashboard-cards-grid">
      {items.map((child, i) => {
        const full =
          items.length === 1 ||
          (items.length % 2 === 1 && i === items.length - 1);
        return (
          <motion.div
            key={(child as any)?.key ?? i}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: i * 0.06 }}
            style={full ? { gridColumn: "1 / -1" } : undefined}
          >
            {child}
          </motion.div>
        );
      })}
    </div>
  );
};

type CardLibrary = Record<
  CardType,
  { label: string; render: (c: InsightCard) => React.ReactNode }
>;

type Props = {
  cards: InsightCard[];
  cardLibrary: CardLibrary;
  onRemove: (id: string) => void;
  onOpenAdd: () => void;
  onEdit: (id: string) => void;
  readOnly?: boolean;
};

const ContentGrid: React.FC<Props> = ({
  cards,
  cardLibrary,
  onRemove,
  onOpenAdd,
  onEdit,
  readOnly = false,
}) => {
  const { t } = useTranslation("dashboard");
  return (
    <Content style={{ padding: 24 }}>
      {cards.length ? (
        <Grid>
          {cards.map((c) => {
            const menu: MenuProps["items"] = [
              {
                key: "edit",
                icon: <EditOutlined />,
                label: t("contentGrid.cardMenu.edit"),
                onClick: () => onEdit(c.id),
              },
              {
                key: "remove",
                icon: <DeleteOutlined />,
                label: t("contentGrid.cardMenu.remove"),
                danger: true,
                onClick: () => onRemove(c.id),
              },
            ];
            return (
              <Card
                key={c.id}
                style={{
                  width: "100%",
                  borderRadius: 12,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  overflow: "hidden",
                }}
                bodyStyle={{
                  padding: 16,
                  minHeight: 210,
                }}
                bordered={false}
              >
                <div style={{ position: "relative" }}>
                  {!readOnly && (
                    <div
                      className="dashboard-card-actions"
                      style={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        zIndex: 1,
                      }}
                    >
                      <Dropdown menu={{ items: menu }} trigger={["click"]}>
                        <Button type="text" icon={<MoreOutlined />} />
                      </Dropdown>
                    </div>
                  )}
                  {cardLibrary[c.type].render(c)}
                </div>
              </Card>
            );
          })}
        </Grid>
      ) : (
        <Empty
          description={t("contentGrid.emptyState.description")}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button type="primary" icon={<PlusOutlined />} onClick={onOpenAdd}>
            {t("contentGrid.emptyState.addButton")}
          </Button>
        </Empty>
      )}
    </Content>
  );
};

export default React.memo(ContentGrid);
